<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import CaptionService from '@/services/CaptionService'
import PageHeader from '@/components/ui/PageHeader.vue'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconElipsis from '@/components/icon/IconElipsis.vue'
import BackButton from '@/components/Atoms/BackButton.vue'
import IconLink from '@/components/icon/IconLink.vue'
import IconLock from '@/components/icon/IconLock.vue'
import IconClock from '@/components/icon/IconClock.vue'
import IconDownload from '@/components/icon/IconDownload.vue'
import CaptionsPreviewPlayer from '@/components/Captioning/CaptionsPreviewPlayer.vue'
import CaptionStatusStrip from '@/components/Captioning/CaptionStatusStrip.vue'
import CaptionSettingsPanel from '@/components/Captioning/CaptionSettingsPanel.vue'
import RecognitionSection from '@/components/Captioning/RecognitionSection.vue'
import CaptionExportSection from '@/components/Captioning/CaptionExportSection.vue'
import SyncWordsGoLiveCard from '@/components/Captioning/SyncWordsGoLiveCard.vue'
import CaptionProviderBanner from '@/components/Captioning/CaptionProviderBanner.vue'
import SyncWordsSettingsPanel from '@/components/Captioning/SyncWordsSettingsPanel.vue'
import {
	FALLBACK_ENGINE_INFO,
	PLAYER_NOTE,
	PROVIDER_ROUTE,
	isSyncWordsFlow,
	isOwnAccountFlow,
	providerProblem,
	syncwordsHeadline,
	flowLanguages,
} from '@/components/Captioning/captionSyncWords'
import {
	FALLBACK_CATALOG,
	summarizeFlowStatus,
	liveLanguages,
	joinLanguageNames,
	isUnreachableStatus,
	windowOf,
	formatCountdown,
	formatDuration,
	durationLabel,
	isProfanitySupported,
	languageName,
} from '@/components/Captioning/captionLanguages'
import { draftFromSettings, settingsFromDraft } from '@/components/Captioning/captionRecognition'
import { isRunningSession } from '@/components/Captioning/captionExport'
import {
	DEFAULT_LIMITS,
	DEFAULT_USAGE,
	UPGRADE_ROUTE,
	hasPool,
	poolExhausted,
	isLimitError,
	limitErrorCopy,
	applyLimitErrorData,
	describeHours,
	formatResetDate,
	billedByProvider,
} from '@/components/Captioning/captionLimits'
import { track, EVENTS } from '@/lib/tracking'

const POLL_MS = 10_000
// While core cannot reach the captions service the poll backs off up to this.
const POLL_MAX_MS = 30_000

const props = defineProps({
	flowId: { type: String, default: '' },
})

const route = useRoute()
const router = useRouter()
const { notify } = useNotification()

const id = computed(() => props.flowId || route.params.flowId)

const loading = ref(true)
const loadError = ref('')
const flow = ref(null)
const status = ref(null)
const statusError = ref('')
const refreshing = ref(false)
// Core answered but could not reach the captions service. Non-blocking: the
// settings stay editable and polling continues with backoff.
const unreachable = ref(false)
const catalog = ref(FALLBACK_CATALOG)

// Plan limits and usage, refreshed from every flow response that carries them
// (an older core never does, so these stay at the defaults: no pool, nothing
// enforced). `usageAt` is the server time of the last read; a running window
// drains the pool from there until the next response re-syncs it.
const limits = ref({ ...DEFAULT_LIMITS })
const usage = ref({ ...DEFAULT_USAGE })
const usageAt = ref(null)

const saving = ref(false)
// { status, code, message, title?, upgrade? } from the last failed save/start/extend.
const serverError = ref(null)
// Server time of the last runner restart (a settings save or Apply now): for
// a minute after it, missing production reads Starting, not Waiting for stream.
const restartedAt = ref(null)
const starting = ref(false)
const stopping = ref(false)
const extending = ref(false)
const lastPayload = ref(null)

// Clock: `skewMs = serverNow - clientNowAtFetch`, re-synced on every response
// that carries `serverNow`. The countdown is `expiresAt - (Date.now() + skew)`
// and is display only; the worker enforces expiry and the next poll reports it.
const skewMs = ref(0)
const clientNow = ref(Date.now())
let clockTimer = null

const player = ref(null)

// Auto-reload bookkeeping: the master playlist only lists subtitle tracks at
// load time, so when a language turns live after the player loaded we reload
// once. `armed` guards against reload loops; it is re-armed by user actions
// (save, resume, manual reload) and on mount.
let liveAtLoad = new Set()
let armed = true
let pollTimer = null
let pollDelay = POLL_MS
let alive = true

// SyncWords flows (engine 'syncwords') get their own settings, status and
// ingest cards; castr flows keep the panels below unchanged.
const isSyncWords = computed(() => isSyncWordsFlow(flow.value))
const engineInfo = ref(FALLBACK_ENGINE_INFO)
// The settings card's draft ({ dirty, valid, settings }): Start sends unsaved settings.
const swPending = ref({ dirty: false, valid: true, settings: null })
// Arrived from the create modal: the go-live card focuses Start.
const justCreated = ref(typeof window !== 'undefined' && window.history?.state?.captionsCreated === true)
// engineState at the last status read, to notice the service turning live.
let lastEngineState = null
watch(isSyncWords, (syncwords) => {
	if (!syncwords) return
	CaptionService.getEngine().then((info) => { if (alive) engineInfo.value = info }).catch(() => {})
}, { immediate: true })

const streamDeleted = computed(() => !!flow.value?.streamDeleted)
const serverNow = computed(() => clientNow.value + skewMs.value)
const headline = computed(() => {
	if (!flow.value) return null
	if (isSyncWords.value) return syncwordsHeadline(flow.value, status.value)
	return summarizeFlowStatus(flow.value, status.value, serverNow.value, { restartedAt: restartedAt.value })
})
const statusUsable = computed(() => !!status.value && !isUnreachableStatus(status.value))
const win = computed(() => windowOf(statusUsable.value ? status.value : null, flow.value))
const running = computed(() => win.value.state === 'running')
const windowRemainingSec = computed(() => {
	if (!running.value || win.value.durationSec === null || !Number.isFinite(win.value.expiresAt)) return null
	return Math.max(0, Math.round((win.value.expiresAt - serverNow.value) / 1000))
})

// Seconds of plan hours left; null for an unlimited pool. Ticks down while
// this window runs, since the window is what consumes them.
const poolRemainingSec = computed(() => {
	const base = usage.value.remainingSec
	if (!Number.isFinite(base) || base === null) return null
	if (!running.value || !Number.isFinite(usageAt.value)) return base
	return Math.max(0, Math.round(base - (serverNow.value - usageAt.value) / 1000))
})
const liveUsage = computed(() => ({ ...usage.value, remainingSec: poolRemainingSec.value }))
// The linked SyncWords account's usage this month (own-account flows), or null.
const providerUsage = ref(null)
// A flow on the customer's own captioning account isn't billed against Castr's plan.
const ownAccount = computed(() => isOwnAccountFlow(flow.value))
const exhausted = computed(() => !ownAccount.value && !billedByProvider(limits.value) && poolExhausted(limits.value, liveUsage.value))
// The own account behind this flow is disconnected or swapped (from status reads).
const accountProblem = computed(() => (ownAccount.value ? providerProblem(status.value) : ''))
const resetDate = computed(() => formatResetDate(limits.value.periodEnd))

// The pool runs dry before the window closes (or the window is unlimited):
// the countdown then counts the hours, not the window.
const poolEndsFirst = computed(() => {
	if (!running.value || !limits.value.enforced || poolRemainingSec.value === null) return false
	return windowRemainingSec.value === null || poolRemainingSec.value < windowRemainingSec.value
})
const remainingSec = computed(() => (poolEndsFirst.value ? poolRemainingSec.value : windowRemainingSec.value))
const countdownLabel = computed(() => {
	if (!running.value) return ''
	if (remainingSec.value === null) return win.value.durationSec === null ? 'Unlimited' : ''
	return remainingSec.value <= 0 ? 'Ended' : formatCountdown(remainingSec.value)
})
// Re-read the session list whenever a window starts, stops or ends.
const exportRefreshKey = computed(() => `${win.value.state}:${win.value.startedAt ?? ''}:${win.value.endedAt ?? ''}`)
// "Download captions" lives in a modal that loads its own list when opened.
// The page only reads the newest session: it tells whether core has the export
// endpoints (404 on an older core hides the entry points) and feeds the
// session card's Last session line.
const exportAvailable = ref(false)
const newestSession = ref(null)
const lastSession = computed(() => (newestSession.value && !isRunningSession(newestSession.value, serverNow.value) ? newestSession.value : null))
const exportOpen = ref(false)
const exportFocusId = ref('')

async function probeExport() {
	const flowId = flow.value?.id
	if (!flowId || streamDeleted.value) return
	try {
		const [newest] = await CaptionService.getSessions(flowId, 1)
		if (!alive || flow.value?.id !== flowId) return
		newestSession.value = newest || null
		exportAvailable.value = true
	} catch (err) {
		if (!alive) return
		// Other failures leave the entry points up; the modal shows the error and Try again.
		exportAvailable.value = err?.status !== 404
	}
}

function openExport(sessionId = '') {
	exportFocusId.value = sessionId
	exportOpen.value = true
}

watch(exportRefreshKey, (next, prev) => {
	if (next !== prev && exportAvailable.value) probeExport()
})
const countdownTitle = computed(() => {
	if (poolEndsFirst.value) return 'Your plan\'s captioning hours run out then'
	return win.value.durationSec === null ? 'Runs until you stop it' : 'Time left in the caption window'
})

/** Takes `limits`/`usage` from a service result that carries them. */
function applyPlan(res) {
	if (!res?.hasLimits) return
	providerUsage.value = res.providerUsage ?? null
	limits.value = res.limits
	usage.value = res.usage
	usageAt.value = serverNow.value
}

// When the window stops, keep what the drain reached instead of snapping back
// to the last server figure; a `pool_exhausted` end means nothing is left.
watch([running, () => win.value.endedReason], ([isRunning, endedReason], [wasRunning]) => {
	if (isRunning || !wasRunning) return
	if (!hasPool(limits.value)) return
	const remaining = endedReason === 'pool_exhausted' ? 0 : poolRemainingSec.value
	if (remaining === null) return
	usage.value = { ...usage.value, remainingSec: remaining, usedSec: Math.max(usage.value.usedSec, limits.value.poolSec - remaining) }
	usageAt.value = serverNow.value
})

/**
 * A plan-limit error from core: folds its numbers into limits/usage, records
 * the cap hit and returns publisher copy for the inline alert; false otherwise.
 */
function limitFailure(err, action) {
	if (!isLimitError(err)) return false
	const next = applyLimitErrorData(err, limits.value, usage.value)
	limits.value = next.limits
	usage.value = next.usage
	usageAt.value = serverNow.value
	track(EVENTS.CAPTIONING_CAP_HIT, { code: err.code, action, surface: 'manage' })
	return limitErrorCopy(err)
}

function failureFor(err, action, fallback) {
	const copy = limitFailure(err, action)
	if (copy) return { action, status: err?.status ?? null, code: err?.code ?? null, message: copy.message, title: copy.title, upgrade: copy.upgrade }
	return { action, status: err?.status ?? null, code: err?.code ?? null, message: err?.message || fallback, errors: Array.isArray(err?.errors) ? err.errors : null }
}

const headlineClass = computed(() => {
	switch (headline.value?.tone) {
		case 'success':
			return 'bg-success-transparent text-success ring-success/30'
		case 'warning':
			return 'bg-surface-3 text-gold ring-gold/30'
		case 'error':
			return 'bg-error-transparent text-error-light ring-error/30'
		default:
			return 'bg-surface-3 text-surface-8 ring-surface-4'
	}
})

/**
 * Applies a status payload from any endpoint, tracking reachability. Polls
 * that find the service unreachable back the interval off (10s, 20s, 30s);
 * the initial load does not, so the first retry is still 10s away.
 */
function applyStatus(next, statusErrorCode = null, { backoff = false } = {}) {
	if (statusErrorCode === 'unreachable' || (next && isUnreachableStatus(next))) {
		unreachable.value = true
		status.value = next || { error: 'unreachable', checkedAt: new Date().toISOString() }
		if (backoff) pollDelay = Math.min(POLL_MAX_MS, pollDelay + POLL_MS)
		return false
	}
	if (next) {
		status.value = next
		if (flow.value && typeof next.configured === 'boolean') flow.value.configured = next.configured
		if (next.engineState) lastEngineState = next.engineState
		syncClock(next.serverNow)
	}
	unreachable.value = false
	pollDelay = POLL_MS
	return true
}

function syncClock(serverNowMs) {
	const n = Number(serverNowMs)
	if (!Number.isFinite(n) || n <= 0) return
	skewMs.value = n - Date.now()
	clientNow.value = Date.now()
}

async function load() {
	const requestedId = id.value
	const seed = CaptionService.cachedFlow(requestedId)
	// The list (or the flow just created) is enough to paint. Status, plan
	// limits, and the clock fill in when the detail request returns.
	if (seed) {
		flow.value = seed
		status.value = null
		unreachable.value = false
		statusError.value = ''
		loadError.value = ''
		loading.value = false
	} else {
		loading.value = true
		loadError.value = ''
	}
	try {
		const res = await CaptionService.getFlow(requestedId)
		if (!alive || id.value !== requestedId) return
		// A save that landed while this request was in flight replaced the
		// seeded object; keep that newer flow and still take status and limits.
		if (!seed || flow.value === seed) flow.value = res.flow
		status.value = null
		unreachable.value = false
		syncClock(res.serverNow)
		applyPlan(res)
		if (res.window && res.status && res.status.window === undefined) res.status.window = res.window
		if (res.status || res.statusError) {
			applyStatus(res.status, res.statusError)
		} else if (flow.value?.configured && !flow.value?.streamDeleted) {
			// Older core without inline status: seed the live set before arming so
			// an already-live language does not trigger a reload and a toast.
			try {
				applyStatus(await CaptionService.getStatus(requestedId))
			} catch (err) {
				if (!alive || id.value !== requestedId) return
				noteStatusFailure(err)
			}
		}
		if (!alive || id.value !== requestedId) return
		liveAtLoad = new Set(liveLanguages(status.value, serverNow.value, flow.value))
		armed = true
		probeExport()
	} catch (err) {
		if (!alive || id.value !== requestedId) return
		if (err?.status === 404) {
			CaptionService.forgetFlow(requestedId)
			flow.value = null
			loadError.value = 'This captioning flow no longer exists.'
			return
		}
		if (!seed) {
			loadError.value = err?.message || 'Could not load this captioning flow.'
		}
	} finally {
		if (alive && id.value === requestedId) loading.value = false
	}
}

function noteStatusFailure(err, { backoff = false } = {}) {
	if (err?.status >= 500 || !err?.status) {
		// Core itself could not be reached, or it proxied a service failure.
		applyStatus(null, 'unreachable', { backoff })
		statusError.value = ''
	} else {
		statusError.value = err?.message || 'Could not check caption status.'
	}
}

async function pollStatus() {
	if (!alive || !flow.value || refreshing.value || streamDeleted.value) return
	refreshing.value = true
	try {
		const next = await CaptionService.getStatus(id.value)
		if (!alive) return
		statusError.value = ''
		const previousEngineState = lastEngineState
		if (applyStatus(next, null, { backoff: true })) evaluateLiveTransition(previousEngineState)
	} catch (err) {
		if (!alive) return
		noteStatusFailure(err, { backoff: true })
	} finally {
		refreshing.value = false
	}
}

function evaluateLiveTransition(previousEngineState = null) {
	if (isSyncWords.value) {
		// The subtitle tracks ride in the stream: reload once so a player that
		// loaded before the captioned feed arrived lists them.
		if (status.value?.engineState !== 'live' || !previousEngineState || previousEngineState === 'live' || !armed) return
		armed = false
		player.value?.reload?.()
		const langs = flowLanguages(flow.value)
		notify({ group: 'success', text: langs.length ? `Captions are live in ${joinLanguageNames(langs, catalog.value)}.` : 'Captions are live.' })
		return
	}
	const live = liveLanguages(status.value, serverNow.value, flow.value)
	const newlyLive = live.filter((l) => !liveAtLoad.has(l))
	if (!running.value || newlyLive.length === 0) return
	if (armed) {
		armed = false
		liveAtLoad = new Set(live)
		player.value?.reload?.()
		notify({ group: 'success', text: `Captions are live in ${joinLanguageNames(newlyLive, catalog.value)}.` })
	}
}

function onPlayerReloaded() {
	// A fresh load has whatever is live right now; re-arm for the next change.
	liveAtLoad = new Set(liveLanguages(status.value, serverNow.value, flow.value))
	armed = true
}

// Poll on a timeout chain (not an interval) so the delay can back off while
// the captions service is unreachable: 10s, 20s, then 30s until it is back.
function scheduleNextPoll() {
	stopPolling()
	if (document.hidden || streamDeleted.value) return
	pollTimer = setTimeout(async () => {
		await pollStatus()
		if (alive) scheduleNextPoll()
	}, pollDelay)
}

function stopPolling() {
	clearTimeout(pollTimer)
	pollTimer = null
}

function onVisibilityChange() {
	if (document.hidden) {
		stopPolling()
	} else {
		pollStatus().then(() => { if (alive) scheduleNextPoll() })
	}
}

/**
 * The saved recognition settings filter profanity but `source` can't: turn the
 * filter off before the session runs in that language. Only `profanity`
 * changes; the rest of the saved block is sent back as it is. The flow's
 * settings are patched in place so the settings panel's draft is not re-seeded.
 */
async function turnOffUnsupportedProfanity(source) {
	const saved = flow.value?.settings
	if (!saved || !saved.profanity || saved.profanity === 'off' || isProfanitySupported(source, catalog.value)) return
	const { settings } = settingsFromDraft({ ...draftFromSettings(saved), profanity: 'off' })
	const res = await CaptionService.updateRecognition(id.value, settings)
	flow.value.settings = res.flow?.settings && Object.keys(res.flow.settings).length ? res.flow.settings : settings
	notify({ group: 'info', text: `Profanity filter turned off: it isn't available for ${languageName(source, catalog.value)}.` })
}

async function save(payload) {
	if (saving.value) return
	lastPayload.value = payload
	saving.value = true
	serverError.value = null
	try {
		await turnOffUnsupportedProfanity(payload.source)
		const res = await CaptionService.updateFlow(id.value, payload)
		flow.value = res.flow
		syncClock(res.serverNow)
		applyPlan(res)
		if (res.status) applyStatus(res.status)
		if (res.restarted) restartedAt.value = serverNow.value
		// The session restarts on any change; the loaded playlist is stale.
		liveAtLoad = new Set()
		armed = true
		track(EVENTS.CAPTIONING_UPDATED, { langs: payload.langs })
		notify({
			group: 'success',
			text: res.restarted
				? 'Settings saved. Captions restart briefly and typically appear within a minute.'
				: 'Settings saved.',
		})
		pollStatus()
	} catch (err) {
		serverError.value = failureFor(err, 'save', 'Could not save the settings.')
	} finally {
		saving.value = false
	}
}

function applySession(res) {
	if (res.flow) flow.value = res.flow
	syncClock(res.serverNow)
	applyPlan(res)
	if (res.status) applyStatus(res.status)
}

async function startWindow({ translations, translationsChanged, ...payload }) {
	if (starting.value) return
	starting.value = true
	serverError.value = null
	try {
		await turnOffUnsupportedProfanity(payload.source)
		// Start carries source/langs only; changed translation settings are
		// saved first with the same PUT a Save uses.
		if (translationsChanged && translations !== undefined) {
			const saved = await CaptionService.updateFlow(id.value, { ...payload, translations })
			if (saved.flow) flow.value = saved.flow
		}
		const res = await CaptionService.startFlow(id.value, payload)
		applySession(res)
		liveAtLoad = new Set()
		armed = true
		const w = windowOf(status.value, flow.value)
		track(EVENTS.CAPTIONING_STARTED, { langs: payload.langs, durationSec: payload.durationSec, windowSec: w.durationSec })
		notify({
			group: 'success',
			text: w.durationSec === null
				? 'Captions started. They typically appear within a minute.'
				: `Captions started for ${durationLabel(w.durationSec)}. They typically appear within a minute.`,
		})
		pollStatus()
	} catch (err) {
		serverError.value = failureFor(err, 'start', 'Could not start captions.')
	} finally {
		starting.value = false
	}
}

async function stopWindow() {
	if (stopping.value) return
	stopping.value = true
	try {
		const res = await CaptionService.stopFlow(id.value)
		applySession(res)
		notify({ group: 'info', text: 'Captions stopped.' })
	} catch (err) {
		const copy = limitFailure(err, 'stop')
		notify({ group: 'error', title: copy ? copy.title : 'Could not stop captions', text: copy ? copy.message : err?.message })
	} finally {
		stopping.value = false
	}
}

async function extendWindow(durationSec) {
	if (extending.value) return
	extending.value = true
	try {
		const res = await CaptionService.extendFlow(id.value, durationSec)
		applySession(res)
		track(EVENTS.CAPTIONING_EXTENDED, { durationSec })
		notify({ group: 'success', text: `Added ${formatDuration(durationSec)} to the window. Captions keep running without a restart.` })
	} catch (err) {
		if (isLimitError(err)) {
			// Plan limit (pool used up, window at its max, rate limited): inline, with the upgrade link.
			serverError.value = failureFor(err, 'extend', 'Could not extend captions.')
		} else if (err?.code === 'window_closed' || err?.status === 409) {
			// The window already ended: reflect it locally so Start is offered, then refresh.
			if (status.value && !isUnreachableStatus(status.value)) {
				status.value = { ...status.value, running: false, window: { ...(status.value.window || {}), state: 'expired', endedReason: 'expired' } }
			}
			notify({ group: 'info', title: 'The caption window already ended', text: 'Start a new window to keep captioning.' })
			pollStatus()
		} else {
			notify({ group: 'error', title: 'Could not extend captions', text: err?.message })
		}
	} finally {
		extending.value = false
	}
}

function retrySave() {
	if (lastPayload.value) save(lastPayload.value)
}

const ingestKey = (f) => `${f?.ingest?.url || ''}|${f?.ingest?.passphrase || ''}`

/** SyncWords settings save (PUT `{ syncwords }`); core refuses it while captions run. */
async function saveSyncWords(settings) {
	if (saving.value) return
	saving.value = true
	serverError.value = null
	const ingestBefore = ingestKey(flow.value)
	try {
		const res = await CaptionService.updateFlow(id.value, { syncwords: settings })
		flow.value = res.flow
		syncClock(res.serverNow)
		applyPlan(res)
		if (res.status) applyStatus(res.status)
		track(EVENTS.CAPTIONING_UPDATED, { langs: res.flow?.langs, engine: 'syncwords' })
		const ingestChanged = !!res.flow?.ingest?.url && ingestKey(res.flow) !== ingestBefore
		notify({
			group: 'success',
			text: ingestChanged ? 'Settings saved. The ingest details changed: update your encoder.' : 'Settings saved.',
		})
	} catch (err) {
		serverError.value = failureFor(err, 'save', 'Could not save the settings.')
	} finally {
		saving.value = false
	}
}

/** Start with the saved settings, or with the settings card's unsaved changes applied first. */
async function startSyncWords({ durationSec }) {
	if (starting.value) return
	starting.value = true
	serverError.value = null
	justCreated.value = false
	const syncwords = swPending.value.dirty ? swPending.value.settings : null
	try {
		const res = await CaptionService.startFlow(id.value, { durationSec, syncwords })
		applySession(res)
		armed = true
		const w = windowOf(status.value, flow.value)
		track(EVENTS.CAPTIONING_STARTED, { langs: flow.value?.langs, durationSec, windowSec: w.durationSec, engine: 'syncwords' })
		notify({
			group: 'success',
			text: 'Captions are starting. The ingest opens in about a minute; start your encoder when step 2 says Ready.',
		})
		pollStatus()
	} catch (err) {
		serverError.value = failureFor(err, 'start', 'Could not start captions.')
	} finally {
		starting.value = false
	}
}

const stopMessage = computed(() => (isSyncWords.value
	? 'This also stops your stream\'s video: it arrives through captioning, so viewers see nothing until you start captions again. You can change caption settings once captions are stopped.'
	: 'Viewers will lose captions until you start a new window.'))
const deleteMessage = computed(() => {
	if (streamDeleted.value) return 'The linked livestream is already gone. This removes the captioning flow.'
	if (isSyncWords.value) return 'Captions stop and the captioning ingest URL is removed. Point your encoder back at Castr to keep streaming.'
	return 'Captions stop for viewers. To use a different stream, create a new flow.'
})

// Recognition saves never restart captions; only the flow and clock refresh.
function onRecognitionSaved(res) {
	if (res.flow) flow.value = res.flow
	syncClock(res.serverNow)
	applyPlan(res)
	if (res.status) applyStatus(res.status)
}

// Turning Speaker labels off saved the translations without gender-aware first.
function onTranslationsCleared(res) {
	if (res.flow) flow.value = res.flow
	syncClock(res.serverNow)
	applyPlan(res)
	if (res.status) applyStatus(res.status)
	if (res.restarted) {
		restartedAt.value = serverNow.value
		liveAtLoad = new Set()
		armed = true
	}
}

// Apply now restarts the running window: the loaded playlist goes stale like after a save.
function onRecognitionApplied(res) {
	applySession(res)
	restartedAt.value = serverNow.value
	liveAtLoad = new Set()
	armed = true
	pollStatus()
}

async function deleteFlow() {
	if (!flow.value) return
	try {
		await CaptionService.deleteFlow(id.value)
		track(EVENTS.CAPTIONING_DELETED, {})
		notify({ group: 'success', text: `Captioning for ${flow.value.streamName || 'this stream'} was deleted.` })
		router.push({ name: 'Captioning' })
	} catch (err) {
		notify({ group: 'error', title: 'Could not delete captioning', text: err?.message })
	}
}

onMounted(async () => {
	CaptionService.getLanguages().then((c) => { if (alive) catalog.value = c }).catch(() => {})
	clockTimer = setInterval(() => { clientNow.value = Date.now() }, 1000)
	await load()
	if (flow.value && !streamDeleted.value) {
		scheduleNextPoll()
		document.addEventListener('visibilitychange', onVisibilityChange)
	}
})

onBeforeUnmount(() => {
	alive = false
	stopPolling()
	clearInterval(clockTimer)
	document.removeEventListener('visibilitychange', onVisibilityChange)
})

watch(id, (next, prev) => {
	if (next && prev && next !== prev) {
		stopPolling()
		load().then(() => { if (flow.value && !streamDeleted.value) scheduleNextPoll() })
	}
})
</script>

<template>
	<div class="container detail-shell px-4">
		<div v-if="loading" class="flex min-h-[60vh] w-full items-center justify-center" aria-busy="true">
			<Spinner text="Loading captioning…" classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>

		<EmptyState v-else-if="loadError" title="Couldn't open this captioning flow" :subtitle="loadError">
			<template #actions>
				<div class="flex gap-2">
					<FormButton type="secondary" size="md" @click="load">Try again</FormButton>
					<FormButton type="main" size="md" :to="{ name: 'Captioning' }">Back to Captioning</FormButton>
				</div>
			</template>
		</EmptyState>

		<main v-else-if="flow" class="flex flex-col gap-4 pb-8">
			<PageHeader :title="flow.streamName || 'Captioning'" show-subtitle-on-mobile class="!mb-0">
				<template #leading>
					<BackButton :to="{ name: 'Captioning' }" title="Back to Captioning" />
				</template>
				<template #subtitle>
					<span v-if="streamDeleted" class="flex min-w-0 max-w-full items-center gap-1.5 text-error-light">
						<icon-base class="shrink-0"><icon-link /></icon-base>
						<span class="min-w-0 truncate" data-testid="linked-stream">Linked livestream removed</span>
					</span>
					<span v-else class="flex min-w-0 max-w-full items-center gap-1.5">
						<icon-base class="shrink-0 text-surface-8"><icon-link /></icon-base>
						<span class="min-w-0 truncate" :title="flow.streamName" data-testid="linked-stream">Linked to {{ flow.streamName }}</span>
						<Tooltip content="Stream can't be changed. Delete this flow and create a new one to caption a different stream." wrapper-class="inline-flex shrink-0">
							<span class="inline-flex items-center gap-1 rounded-full bg-surface-3 px-2 py-0.5 text-[11px] text-surface-7" aria-label="Stream can't be changed">
								<icon-base width="11" height="11" fill="none"><icon-lock /></icon-base>
								<span class="hidden sm:inline">Stream can't be changed</span>
							</span>
						</Tooltip>
					</span>
				</template>

				<span
					v-if="headline"
					class="inline-flex h-8 items-center gap-2 rounded-full px-3 text-tiny font-medium ring-1"
					:class="headlineClass"
					role="status"
					:data-status="headline.key"
					:title="headline.tooltip || undefined"
				>
					<span
						class="h-2 w-2 shrink-0 rounded-full"
						:class="[headline.tone === 'success' ? 'bg-success animate-pulse' : headline.tone === 'warning' ? 'bg-gold' : headline.tone === 'error' ? 'bg-error' : 'bg-surface-6']"
						aria-hidden="true"
					/>
					{{ headline.label }}
				</span>
				<span
					v-if="countdownLabel && !streamDeleted && !isSyncWords"
					class="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-tiny font-medium tabular-nums ring-1"
					:class="poolEndsFirst ? 'bg-surface-3 text-gold ring-gold/30' : 'bg-surface-3 text-surface-text-2 ring-surface-4'"
					:title="countdownTitle"
					:data-pool-ends-first="poolEndsFirst ? 'true' : undefined"
				>
					<icon-base width="12" height="12" :class="poolEndsFirst ? 'text-gold' : 'text-surface-7'"><icon-clock /></icon-base>
					<span data-testid="header-countdown">{{ countdownLabel }}</span>
				</span>
				<drop-down-menu type="secondary" size="md" is-icon position="right" aria-label="More actions" list-classes="min-w-[180px]">
					<template #toggle-button>
						<icon-base><icon-elipsis /></icon-base>
					</template>
					<template #menu-items>
						<drop-down-menu-item v-if="!streamDeleted && exportAvailable" data-testid="menu-download-captions" @click="openExport()">
							Download captions
						</drop-down-menu-item>
						<drop-down-menu-item v-if="!streamDeleted && running" :disabled="stopping" @click="showModal('modal-captioning-stop')">
							Stop captions
						</drop-down-menu-item>
						<drop-down-menu-item
							custom-classes="h-10 md:h-8 !p-3 md:!p-2 hover:bg-surface-3 text-error-light hover:text-error-light text-nowrap leading-none text-base md:text-tiny flex items-center w-full truncate"
							@click="showModal('modal-captioning-delete')"
						>
							Delete
						</drop-down-menu-item>
					</template>
				</drop-down-menu>
			</PageHeader>

			<!-- The linked stream is gone: nothing to configure or preview. -->
			<section v-if="streamDeleted" class="rounded-xl bg-surface-2 px-6 py-10" data-testid="stream-removed">
				<EmptyState
					title="The linked livestream was removed"
					subtitle="Captions no longer run for this flow, and a flow can't be moved to another stream. Delete it, then create a new captioning flow for the stream you want to caption."
					min-height="0"
				>
					<template #icon>
						<icon-base width="28" height="28" class="text-error-light"><icon-link /></icon-base>
					</template>
					<template #actions>
						<FormButton type="red" size="md" @click="showModal('modal-captioning-delete')">Delete captioning</FormButton>
					</template>
				</EmptyState>
			</section>

			<template v-else>
				<Alert v-if="unreachable" type="warning2" role="status" data-testid="unreachable-banner">
					The captioning service isn't reachable right now. Your settings are saved; status will resume when it's back.
				</Alert>
				<Alert v-if="exhausted" type="warning2" role="status" data-testid="pool-exhausted-alert">
					<span class="flex w-full flex-wrap items-center gap-x-1 gap-y-1">
						<span>
							You've used all {{ describeHours(limits.poolSec) }} of captioning this cycle<template v-if="resetDate"> · resets {{ resetDate }}</template>.
							Captions can't start or be extended until then.
						</span>
						<router-link :to="UPGRADE_ROUTE" class="font-medium text-white underline" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning' })">
							Upgrade for more hours
						</router-link>
					</span>
				</Alert>

				<!-- Ingest-based flows: the encoder target first, then the castr-like grid without
				     the multi-language, recognition and Extend controls. -->
				<template v-if="isSyncWords">
					<CaptionProviderBanner :provider="engineInfo.provider" />
					<Alert v-if="accountProblem" type="error" role="alert" data-vendor-name data-testid="provider-problem">
						<span class="flex w-full flex-wrap items-center gap-x-1 gap-y-1">
							<span v-if="accountProblem === 'mismatch'">This captioning was created on a different SyncWords account than the one connected now. Reconnect that account, or delete this captioning and create it again.</span>
							<span v-else>This captioning runs on your SyncWords account, which is no longer connected. Reconnect it to start captions.</span>
							<router-link :to="PROVIDER_ROUTE" class="font-medium text-white underline">Reconnect</router-link>
						</span>
					</Alert>
					<SyncWordsGoLiveCard
						:flow="flow"
						:status="status"
						:info="engineInfo"
						:skew-ms="skewMs"
						:remaining-sec="remainingSec"
						:pool-ends-first="poolEndsFirst"
						:error="statusError"
						:unreachable="unreachable"
						:limits="limits"
						:usage="liveUsage"
						:starting="starting"
						:stopping="stopping"
						:pending="swPending"
						:provider-usage="providerUsage"
						:server-error="serverError"
						:focus-start="justCreated && !running"
						@start="startSyncWords"
						@stop="showModal('modal-captioning-stop')"
					/>
					<section class="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-start" data-testid="captioning-layout">
						<div class="flex min-w-0 flex-col gap-4 lg:col-span-7" data-testid="layout-left">
							<CaptionsPreviewPlayer
								ref="player"
								:embed-url="flow.playback?.embed || ''"
								:stream-name="flow.streamName"
								:note="PLAYER_NOTE"
								@reloaded="onPlayerReloaded"
							>
								<template #actions>
									<FormButton v-if="exportAvailable" type="secondary" size="sm" data-testid="player-download" @click="openExport(lastSession ? lastSession.id : '')">
										<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16" class="mr-1.5 text-surface-7"><icon-download /></icon-base>
										Download captions
									</FormButton>
								</template>
							</CaptionsPreviewPlayer>
						</div>
						<div class="min-w-0 lg:col-span-5" data-testid="layout-settings">
							<SyncWordsSettingsPanel
								:flow="flow"
								:info="engineInfo"
								:saving="saving"
								:starting="starting"
								:server-error="serverError"
								:window="win"
								:limits="limits"
								@save="saveSyncWords"
								@pending="swPending = $event"
								@stop="showModal('modal-captioning-stop')"
							/>
						</div>
					</section>
				</template>

				<!-- Both cells stretch to the taller one; the settings card absorbs the
				     difference above its footer. The session card only stretches while
				     running; off, it hugs its content rather than show an empty block. -->
				<section v-else class="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-stretch" data-testid="captioning-layout">
					<div class="flex min-w-0 flex-col gap-4 lg:col-span-7" data-testid="layout-left">
						<CaptionsPreviewPlayer
							ref="player"
							:embed-url="flow.playback?.embed || ''"
							:stream-name="flow.streamName"
							@reloaded="onPlayerReloaded"
						/>
						<CaptionStatusStrip
							:class="running ? 'lg:flex-1' : ''"
							:flow="flow"
							:status="status"
							:catalog="catalog"
							:skew-ms="skewMs"
							:remaining-sec="remainingSec"
							:pool-ends-first="poolEndsFirst"
							:error="statusError"
							:unreachable="unreachable"
							:restarted-at="restartedAt"
							controls
							:limits="limits"
							:usage="liveUsage"
							:extending="extending"
							:stopping="stopping"
							:last-session="lastSession"
							:downloadable="exportAvailable"
							@stop="showModal('modal-captioning-stop')"
							@extend="extendWindow"
							@download="openExport"
						/>
					</div>
					<div class="min-w-0 lg:col-span-5" data-testid="layout-settings">
						<CaptionSettingsPanel
							class="h-full"
							:flow="flow"
							:catalog="catalog"
							:saving="saving"
							:server-error="serverError"
							:window="win"
							:limits="limits"
							:usage="liveUsage"
							:starting="starting"
							@save="save"
							@retry="retrySave"
							@start="startWindow"
						/>
					</div>
				</section>

				<RecognitionSection
					v-if="!isSyncWords"
					:flow="flow"
					:running="running"
					:catalog="catalog"
					@saved="onRecognitionSaved"
					@applied="onRecognitionApplied"
					@translations-cleared="onTranslationsCleared"
				/>
			</template>
		</main>

		<ConfirmModal
			modal-id="modal-captioning-stop"
			:title="isSyncWords ? 'Stop captions and the stream\'s video?' : 'Stop captions?'"
			:message="stopMessage"
			ok-text="Stop captions"
			cancel-text="Keep running"
			modal-type="danger"
			@modal-confirm="stopWindow"
		/>
		<ConfirmModal
			modal-id="modal-captioning-delete"
			:title="`Delete captioning for ${flow?.streamName || 'this stream'}?`"
			:message="deleteMessage"
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="deleteFlow"
		/>
		<ModalBox
			v-if="flow"
			v-model="exportOpen"
			title="Download captions"
			size="sm"
			scroll-body
			mobile-placement="bottom"
			wrapper-classes="lg:!w-[560px] lg:max-h-[80vh]"
			body-classes="px-4 pb-4 sm:px-6 sm:pb-6"
		>
			<CaptionExportSection
				:flow-id="flow.id"
				:stream-key="flow.streamKey"
				:catalog="catalog"
				:skew-ms="skewMs"
				:refresh-key="exportRefreshKey"
				:focus-session-id="exportFocusId"
				@unavailable="exportAvailable = false"
			/>
		</ModalBox>
	</div>
</template>
